import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

const FILTERS = [
  ["pending", "Onay Bekleyenler"],
  ["approved", "Onaylananlar"],
  ["rejected", "Reddedilenler"],
  ["all", "Tümü"],
];

const COLORS = {
  navy: "#102a43",
  blue: "#1463b8",
  orange: "#f28c28",
  green: "#168a4a",
  red: "#b91c1c",
  muted: "#627d98",
  border: "#d9e2ec",
  surface: "#f4f7fc",
};

async function readPayload(response) {
  const raw = await response.text();

  if (!raw) {
    return {};
  }

  try {
    return JSON.parse(raw);
  } catch {
    return {
      message: raw,
    };
  }
}

function listFrom(payload) {
  if (
    Array.isArray(
      payload?.data?.items
    )
  ) {
    return payload.data.items;
  }

  if (
    Array.isArray(
      payload?.data
    )
  ) {
    return payload.data;
  }

  if (
    Array.isArray(
      payload?.items
    )
  ) {
    return payload.items;
  }

  if (Array.isArray(payload)) {
    return payload;
  }

  return [];
}

function numberValue(value) {
  return new Intl.NumberFormat(
    "tr-TR"
  ).format(
    Number(value || 0)
  );
}

function dateValue(value) {
  if (!value) {
    return "—";
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "—";
  }

  return new Intl.DateTimeFormat(
    "tr-TR",
    {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }
  ).format(date);
}

function taskTypeText(type) {
  const labels = {
    daily_login:
      "Günlük giriş görevi",

    sponsor_video:
      "Sponsor video",

    instagram_code:
      "Instagram kampanya kodu",

    website_code:
      "Web sitesi kampanya kodu",

    survey:
      "Anket katılımı",

    content_read:
      "İçerik okuma",

    qr_visit:
      "QR ziyaret",

    profile_complete:
      "Profil tamamlama",

    phone_verify:
      "Telefon doğrulama",

    purchase:
      "Alışveriş",

    referral:
      "Üye daveti",
  };

  return (
    labels[type] ||
    type ||
    "Görev"
  );
}

function targetTypeText(value) {
  const labels = {
    all: "Herkes",

    institution:
      "Belirli kurum",

    country:
      "Belirli ülke",

    city:
      "Belirli şehir",

    district:
      "Belirli ilçe",

    group:
      "Belirli grup",

    new_users:
      "Yeni kullanıcılar",
  };

  return (
    labels[value] ||
    value ||
    "Herkes"
  );
}

function statusText(status) {
  if (status === "approved") {
    return "Onaylandı ve Yayında";
  }

  if (status === "rejected") {
    return "Reddedildi";
  }

  return "Onay Bekliyor";
}

function statusStyle(status) {
  const base = {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 28,
    padding: "0 10px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 900,
    whiteSpace: "nowrap",
  };

  if (status === "approved") {
    return {
      ...base,
      color: "#166534",
      background: "#dcfce7",
    };
  }

  if (status === "rejected") {
    return {
      ...base,
      color: "#991b1b",
      background: "#fee2e2",
    };
  }

  return {
    ...base,
    color: "#92400e",
    background: "#fef3c7",
  };
}

function Detail({
  label,
  value,
}) {
  return (
    <div
      style={{
        padding: "10px 11px",
        borderRadius: 12,
        background:
          COLORS.surface,
        border:
          `1px solid ${COLORS.border}`,
      }}
    >
      <span
        style={{
          display: "block",
          color: COLORS.muted,
          fontSize: 10,
          fontWeight: 800,
        }}
      >
        {label}
      </span>

      <b
        style={{
          display: "block",
          marginTop: 4,
          color: COLORS.navy,
          fontSize: 13,
          overflowWrap:
            "anywhere",
        }}
      >
        {value || "—"}
      </b>
    </div>
  );
}

export default function MespuanGorevOnaylari({
  API,
  token,
  setMessage,
}) {
  const [
    items,
    setItems,
  ] = useState([]);

  const [
    filter,
    setFilter,
  ] = useState("pending");

  const [
    search,
    setSearch,
  ] = useState("");

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    actionId,
    setActionId,
  ] = useState(null);

  const [
    openById,
    setOpenById,
  ] = useState({});

  const [
    notes,
    setNotes,
  ] = useState({});

  const fetchTasks =
    useCallback(async () => {
      if (!token) {
        return;
      }

      setLoading(true);

      try {
        const params =
          new URLSearchParams({
            page: "1",
            limit: "100",
          });

        if (filter !== "all") {
          params.set(
            "status",
            filter
          );
        }

        const response =
          await fetch(
            `${API}/mespuan/admin/tasks?${params.toString()}`,
            {
              headers: {
                Authorization:
                  `Bearer ${token}`,

                Accept:
                  "application/json",
              },
            }
          );

        const payload =
          await readPayload(
            response
          );

        if (!response.ok) {
          throw new Error(
            payload?.message ||
              "MesPuan görevleri alınamadı."
          );
        }

        setItems(
          listFrom(payload)
        );
      } catch (error) {
        setItems([]);

        setMessage?.({
          type: "error",
          text:
            error.message ||
            "MesPuan görevleri alınamadı.",
        });
      } finally {
        setLoading(false);
      }
    }, [
      API,
      filter,
      setMessage,
      token,
    ]);

  useEffect(() => {
    fetchTasks();

    const timer =
      window.setInterval(
        fetchTasks,
        20000
      );

    return () => {
      window.clearInterval(
        timer
      );
    };
  }, [fetchTasks]);

  const visibleItems =
    useMemo(() => {
      const term =
        search
          .trim()
          .toLocaleLowerCase(
            "tr-TR"
          );

      if (!term) {
        return items;
      }

      return items.filter(
        (item) =>
          [
            item.title,
            item.description,
            item.institution_id,
            item.type,
            item.destination_url,
            item.target_value,
          ]
            .filter(Boolean)
            .join(" ")
            .toLocaleLowerCase(
              "tr-TR"
            )
            .includes(term)
      );
    }, [
      items,
      search,
    ]);

  async function decideTask(
    item,
    decision
  ) {
    const note =
      String(
        notes[item.id] || ""
      ).trim();

    if (
      decision === "reject" &&
      note.length < 5
    ) {
      setMessage?.({
        type: "error",
        text:
          "Reddetmek için en az 5 karakterlik gerekçe yazmalısınız.",
      });

      return;
    }

    const verb =
      decision === "approve"
        ? "onaylamak"
        : "reddetmek";

    const confirmed =
      window.confirm(
        `“${item.title}” görevini ${verb} istiyor musunuz?`
      );

    if (!confirmed) {
      return;
    }

    setActionId(item.id);

    try {
      const response =
        await fetch(
          `${API}/mespuan/admin/tasks/${item.id}/review`,
          {
            method: "POST",

            headers: {
              Authorization:
                `Bearer ${token}`,

              "Content-Type":
                "application/json",
            },

            body:
              JSON.stringify({
                action:
                  decision,

                review_note:
                  note || null,
              }),
          }
        );

      const payload =
        await readPayload(
          response
        );

      if (!response.ok) {
        throw new Error(
          payload?.message ||
            "Görev onay işlemi tamamlanamadı."
        );
      }

      setMessage?.({
        type: "success",

        text:
          decision === "approve"
            ? "Görev onaylandı. Mobil uygulamada yayınlanacaktır."
            : "Görev reddedildi.",
      });

      setNotes(
        (previous) => ({
          ...previous,
          [item.id]: "",
        })
      );

      await fetchTasks();
    } catch (error) {
      setMessage?.({
        type: "error",
        text:
          error.message ||
          "İşlem tamamlanamadı.",
      });
    } finally {
      setActionId(null);
    }
  }

  return (
    <section
      style={{
        marginTop: 26,
      }}
    >
      <div
        style={{
          padding: 22,
          borderRadius: 24,
          background: "#fff",
          border:
            `1px solid ${COLORS.border}`,
          boxShadow:
            "0 18px 45px rgba(16,42,67,.08)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent:
              "space-between",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div>
            <div
              style={{
                color:
                  COLORS.orange,

                fontSize: 11,
                fontWeight: 900,
                letterSpacing:
                  ".09em",
              }}
            >
              MESYAT GÖREV DOĞRULAMA MERKEZİ
            </div>

            <h2
              style={{
                margin:
                  "6px 0 5px",

                color:
                  COLORS.navy,

                fontSize: 25,
              }}
            >
              MesPuan Görev Onayları
              {" "}
              ({items.length})
            </h2>

            <p
              style={{
                margin: 0,
                color:
                  COLORS.muted,

                lineHeight: 1.5,
              }}
            >
              Kurumların oluşturduğu veya
              güncellediği görevleri inceleyin,
              onaylayın ya da gerekçesiyle
              reddedin.
            </p>
          </div>

          <button
            type="button"
            onClick={fetchTasks}
            disabled={loading}
            style={{
              alignSelf:
                "flex-start",

              padding:
                "10px 14px",

              borderRadius: 12,
              border: 0,
              color: "#fff",
              background:
                COLORS.blue,

              fontWeight: 900,
              cursor: "pointer",
            }}
          >
            {loading
              ? "Yükleniyor..."
              : "Yenile"}
          </button>
        </div>

        <div
          style={{
            display: "flex",
            gap: 8,
            marginTop: 18,
            flexWrap: "wrap",
          }}
        >
          {FILTERS.map(
            ([
              value,
              label,
            ]) => (
              <button
                type="button"
                key={value}
                onClick={() =>
                  setFilter(
                    value
                  )
                }
                style={{
                  padding:
                    "9px 12px",

                  borderRadius:
                    999,

                  border:
                    filter ===
                    value
                      ? `1px solid ${COLORS.blue}`
                      : `1px solid ${COLORS.border}`,

                  color:
                    filter ===
                    value
                      ? "#fff"
                      : COLORS.navy,

                  background:
                    filter ===
                    value
                      ? COLORS.blue
                      : "#fff",

                  fontWeight:
                    900,

                  cursor:
                    "pointer",
                }}
              >
                {label}
              </button>
            )
          )}

          <input
            value={search}
            onChange={(event) =>
              setSearch(
                event.target.value
              )
            }
            placeholder="Kurum, görev veya bağlantı ara..."
            style={{
              flex:
                "1 1 260px",

              minHeight: 40,
              padding:
                "0 12px",

              borderRadius: 12,
              border:
                `1px solid ${COLORS.border}`,
            }}
          />
        </div>

        <div
          style={{
            display: "grid",
            gap: 13,
            marginTop: 18,
          }}
        >
          {!loading &&
            visibleItems.length ===
              0 && (
              <div
                style={{
                  padding: 30,
                  textAlign:
                    "center",

                  borderRadius:
                    18,

                  color:
                    COLORS.muted,

                  background:
                    COLORS.surface,
                }}
              >
                <b
                  style={{
                    display:
                      "block",

                    color:
                      COLORS.navy,

                    marginBottom:
                      5,
                  }}
                >
                  Bu görünümde görev bulunmuyor
                </b>

                Yeni veya güncellenen kurum
                görevleri burada onaya
                gelecektir.
              </div>
            )}

          {visibleItems.map(
            (item) => {
              const open =
                Boolean(
                  openById[
                    item.id
                  ]
                );

              const busy =
                actionId ===
                item.id;

              return (
                <article
                  key={item.id}
                  style={{
                    overflow:
                      "hidden",

                    borderRadius:
                      18,

                    border:
                      `1px solid ${COLORS.border}`,

                    background:
                      "#fff",
                  }}
                >
                  <button
                    type="button"
                    onClick={() =>
                      setOpenById(
                        (
                          previous
                        ) => ({
                          ...previous,

                          [item.id]:
                            !previous[
                              item.id
                            ],
                        })
                      )
                    }
                    style={{
                      width:
                        "100%",

                      display:
                        "flex",

                      justifyContent:
                        "space-between",

                      gap: 14,
                      alignItems:
                        "center",

                      padding: 15,
                      border: 0,
                      background:
                        "#fff",

                      textAlign:
                        "left",

                      cursor:
                        "pointer",
                    }}
                  >
                    <span
                      style={{
                        minWidth:
                          0,
                      }}
                    >
                      <b
                        style={{
                          display:
                            "block",

                          color:
                            COLORS.navy,

                          fontSize:
                            16,
                        }}
                      >
                        {item.title ||
                          "İsimsiz görev"}
                      </b>

                      <span
                        style={{
                          display:
                            "block",

                          color:
                            COLORS.muted,

                          fontSize:
                            12,

                          marginTop:
                            4,
                        }}
                      >
                        Kurum #
                        {item.institution_id ||
                          "Genel"}
                        {" · "}
                        {taskTypeText(
                          item.type
                        )}
                        {" · "}
                        {dateValue(
                          item.submitted_at ||
                            item.updated_at ||
                            item.created_at
                        )}
                      </span>
                    </span>

                    <span
                      style={statusStyle(
                        item.approval_status
                      )}
                    >
                      {statusText(
                        item.approval_status
                      )}
                    </span>
                  </button>

                  {open && (
                    <div
                      style={{
                        padding: 15,

                        borderTop:
                          `1px solid ${COLORS.border}`,

                        background:
                          "#fbfdff",
                      }}
                    >
                      <div
                        style={{
                          display:
                            "grid",

                          gridTemplateColumns:
                            "repeat(auto-fit,minmax(145px,1fr))",

                          gap: 9,
                        }}
                      >
                        <Detail
                          label="Kurum"
                          value={
                            item.institution_id
                              ? `Kurum #${item.institution_id}`
                              : "Genel görev"
                          }
                        />

                        <Detail
                          label="Görev türü"
                          value={taskTypeText(
                            item.type
                          )}
                        />

                        <Detail
                          label="MesPuan ödülü"
                          value={`${numberValue(
                            item.reward_points
                          )} MP`}
                        />

                        <Detail
                          label="Günlük sınır"
                          value={
                            item.daily_limit ||
                            1
                          }
                        />

                        <Detail
                          label="Hedef kitle"
                          value={targetTypeText(
                            item.target_type
                          )}
                        />

                        <Detail
                          label="Toplam katılım"
                          value={
                            item.total_completion_limit
                              ? numberValue(
                                  item.total_completion_limit
                                )
                              : "Sınırsız"
                          }
                        />
                      </div>

                      {item.description && (
                        <p
                          style={{
                            margin:
                              "13px 0 0",

                            color:
                              COLORS.muted,

                            lineHeight:
                              1.6,
                          }}
                        >
                          {
                            item.description
                          }
                        </p>
                      )}

                      {item.destination_url && (
                        <a
                          href={
                            item.destination_url
                          }
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            display:
                              "inline-block",

                            marginTop:
                              12,

                            color:
                              COLORS.blue,

                            fontWeight:
                              800,

                            overflowWrap:
                              "anywhere",
                          }}
                        >
                          Görev bağlantısını aç
                        </a>
                      )}

                      {item.review_note && (
                        <div
                          style={{
                            marginTop:
                              13,

                            padding:
                              12,

                            borderRadius:
                              12,

                            color:
                              COLORS.navy,

                            background:
                              "#eef4fb",

                            border:
                              "1px solid #cfe0f3",
                          }}
                        >
                          <b>
                            Yönetici notu:
                          </b>{" "}
                          {
                            item.review_note
                          }
                        </div>
                      )}

                      {item.approval_status ===
                        "pending" && (
                        <div
                          style={{
                            marginTop:
                              14,
                          }}
                        >
                          <textarea
                            value={
                              notes[
                                item.id
                              ] || ""
                            }
                            onChange={(
                              event
                            ) =>
                              setNotes(
                                (
                                  previous
                                ) => ({
                                  ...previous,

                                  [item.id]:
                                    event
                                      .target
                                      .value,
                                })
                              )
                            }
                            placeholder="Onay notu veya zorunlu ret gerekçesi..."
                            style={{
                              width:
                                "100%",

                              minHeight:
                                78,

                              resize:
                                "vertical",

                              padding:
                                12,

                              borderRadius:
                                12,

                              border:
                                `1px solid ${COLORS.border}`,

                              fontFamily:
                                "inherit",
                            }}
                          />

                          <div
                            style={{
                              display:
                                "flex",

                              justifyContent:
                                "flex-end",

                              gap: 9,
                              marginTop:
                                9,

                              flexWrap:
                                "wrap",
                            }}
                          >
                            <button
                              type="button"
                              disabled={
                                busy
                              }
                              onClick={() =>
                                decideTask(
                                  item,
                                  "reject"
                                )
                              }
                              style={{
                                padding:
                                  "10px 14px",

                                borderRadius:
                                  12,

                                border:
                                  "1px solid #fecaca",

                                color:
                                  COLORS.red,

                                background:
                                  "#fff1f2",

                                fontWeight:
                                  900,

                                cursor:
                                  "pointer",
                              }}
                            >
                              Reddet
                            </button>

                            <button
                              type="button"
                              disabled={
                                busy
                              }
                              onClick={() =>
                                decideTask(
                                  item,
                                  "approve"
                                )
                              }
                              style={{
                                padding:
                                  "10px 14px",

                                borderRadius:
                                  12,

                                border:
                                  0,

                                color:
                                  "#fff",

                                background:
                                  COLORS.green,

                                fontWeight:
                                  900,

                                cursor:
                                  "pointer",
                              }}
                            >
                              {busy
                                ? "İşleniyor..."
                                : "Görevi Onayla"}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </article>
              );
            }
          )}
        </div>
      </div>
    </section>
  );
}